import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';

// Define form state types
interface FormState {
  name: string;
  email: string;
  vendorId: string;
  subject: string;
  message: string;
  priority: 'low' | 'medium' | 'high' | 'critical';
}

// Define initial form state
const INITIAL_FORM_STATE: FormState = {
  name: '',
  email: '',
  vendorId: '',
  subject: '',
  message: '',
  priority: 'medium',
};

const FAQ_ITEMS = [
  {
    question: "How do I reset my vendor API key?",
    answer: "To reset your API key, log in to the Vendor Portal, navigate to Settings > API Keys, and click the 'Regenerate Key' button. Be sure to update all services using the old key immediately, as the previous key will be invalidated."
  },
  {
    question: "What formats are supported for test data submission?",
    answer: "ReasonShield currently supports JSON formatted data for all API submissions. The specific schema for each test type is documented in the API Documentation. We recommend validating your data against our JSON schema before submission."
  },
  {
    question: "How can I test my integration without affecting production data?",
    answer: "We provide a sandbox environment for all vendors. To access it, use your existing API credentials but with the sandbox base URL (https://sandbox.reasonshield.com/api/v1). All operations in the sandbox environment use test data and do not affect production systems."
  },
  {
    question: "What should I do if I receive an 'INVALID_DATA_FORMAT' error?",
    answer: "This error indicates that your submitted data does not match the expected schema. Check the error details for specific field validation issues, verify your data structure against our documentation, and ensure all required fields are present and correctly formatted."
  }
];

const VendorSupportPage: React.FC = () => {
  const [formState, setFormState] = useState<FormState>(INITIAL_FORM_STATE);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormState(prev => ({ ...prev, [name]: value }));
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    
    try {
      // Here you would typically send the form data to your API
      // This is a simulated API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Simulate successful submission
      setSubmitted(true);
      setFormState(INITIAL_FORM_STATE);
    } catch (err) {
      setError('There was an error submitting your support request. Please try again or contact us directly.');
      console.error('Form submission error:', err);
    } finally {
      setSubmitting(false);
    }
  };
  
  return (
    <div className="container-custom py-16">
      <Helmet>
        <title>ReasonShield Vendor Support | Technical Help & Resources</title>
        <meta 
          name="description" 
          content="Get technical support for your ReasonShield vendor integration. Submit support tickets, browse FAQs, and access resources to solve common issues." 
        />
      </Helmet>
      
      <h1 className="text-4xl md:text-5xl font-bold mb-4">Vendor Support</h1>
      <p className="text-xl mb-8 text-text">
        Technical assistance and resources for ReasonShield vendor partners.
      </p>
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
        <div className="lg:col-span-2">
          <section className="bg-background-alt p-8 rounded-lg mb-8">
            <h2 className="text-2xl font-bold mb-6 text-primary">Submit a Support Request</h2>
            
            {submitted ? (
              <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4" role="alert">
                <p className="font-bold">Request Submitted</p>
                <p>Thank you! Your support request has been received. We'll respond to you within 24 hours.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                {error && (
                  <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4" role="alert">
                    <p className="font-bold">Error</p>
                    <p>{error}</p>
                  </div>
                )}
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-text mb-1">
                      Name
                    </label>
                    <input
                      type="text"
                      name="name"
                      id="name"
                      required
                      value={formState.name}
                      onChange={handleInputChange}
                      className="w-full border-border rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary focus:border-primary"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-text mb-1">
                      Email
                    </label>
                    <input
                      type="email"
                      name="email"
                      id="email"
                      required
                      value={formState.email}
                      onChange={handleInputChange}
                      className="w-full border-border rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary focus:border-primary"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="vendorId" className="block text-sm font-medium text-text mb-1">
                      Vendor ID
                    </label>
                    <input
                      type="text"
                      name="vendorId"
                      id="vendorId"
                      required
                      value={formState.vendorId}
                      onChange={handleInputChange}
                      className="w-full border-border rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary focus:border-primary"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="priority" className="block text-sm font-medium text-text mb-1">
                      Priority
                    </label>
                    <select
                      name="priority"
                      id="priority"
                      required
                      value={formState.priority}
                      onChange={handleInputChange}
                      className="w-full border-border rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary focus:border-primary"
                    >
                      <option value="low">Low - General Question</option>
                      <option value="medium">Medium - Minor Issue</option>
                      <option value="high">High - Significant Problem</option>
                      <option value="critical">Critical - Production Outage</option>
                    </select>
                  </div>
                </div>
                
                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-text mb-1">
                    Subject
                  </label>
                  <input
                    type="text"
                    name="subject"
                    id="subject"
                    required
                    value={formState.subject}
                    onChange={handleInputChange}
                    className="w-full border-border rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary focus:border-primary"
                  />
                </div>
                
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-text mb-1">
                    Message
                  </label>
                  <textarea
                    name="message"
                    id="message"
                    rows={6}
                    required
                    value={formState.message}
                    onChange={handleInputChange}
                    className="w-full border-border rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary focus:border-primary"
                    placeholder="Please describe your issue in detail, including any error messages, the steps to reproduce the problem, and what you've already tried."
                  ></textarea>
                </div>
                
                <div>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="btn btn-primary w-full md:w-auto"
                  >
                    {submitting ? 'Submitting...' : 'Submit Support Request'}
                  </button>
                </div>
              </form>
            )}
          </section>
          
          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-6 text-primary">Common Issues & Solutions</h2>
            
            <div className="space-y-4">
              {FAQ_ITEMS.map((item, index) => (
                <div key={index} className="bg-background-alt p-6 rounded-lg">
                  <h3 className="text-xl font-semibold mb-2 text-primary">{item.question}</h3>
                  <p className="text-text">{item.answer}</p>
                </div>
              ))}
            </div>
          </section>
        </div>
        
        <div className="lg:col-span-1 space-y-8">
          <div className="bg-background-alt p-6 rounded-lg sticky top-24">
            <h3 className="text-xl font-semibold mb-4 text-primary">Quick Resources</h3>
            
            <div className="space-y-4">
              <Link to="/vendor-documentation" className="block p-4 border border-border rounded-lg hover:border-primary">
                <h4 className="font-semibold text-lg mb-1">API Documentation</h4>
                <p className="text-sm text-text">Complete reference for all API endpoints</p>
              </Link>
              
              <Link to="/vendor-integration" className="block p-4 border border-border rounded-lg hover:border-primary">
                <h4 className="font-semibold text-lg mb-1">Integration Guide</h4>
                <p className="text-sm text-text">Step-by-step implementation tutorials</p>
              </Link>
              
              <a href="https://github.com/reasonshield/api-examples" target="_blank" rel="noopener noreferrer" className="block p-4 border border-border rounded-lg hover:border-primary">
                <h4 className="font-semibold text-lg mb-1">Code Examples</h4>
                <p className="text-sm text-text">Sample implementations in multiple languages</p>
              </a>
              
              <a href="https://app.reasonshield.com/vendor-portal" target="_blank" rel="noopener noreferrer" className="block p-4 border border-border rounded-lg hover:border-primary">
                <h4 className="font-semibold text-lg mb-1">Vendor Portal</h4>
                <p className="text-sm text-text">Manage your account and credentials</p>
              </a>
            </div>
            
            <hr className="my-6 border-border" />
            
            <h3 className="text-xl font-semibold mb-4 text-primary">Contact Options</h3>
            
            <div className="space-y-4">
              <div className="flex items-start">
                <svg className="w-5 h-5 text-primary mt-0.5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                  <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                </svg>
                <div>
                  <h4 className="font-semibold mb-1">Email Support</h4>
                  <a href="mailto:api-support@reasonshield.com" className="text-primary hover:underline">api-support@reasonshield.com</a>
                  <p className="text-sm text-text mt-1">Response within 24 hours</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <svg className="w-5 h-5 text-primary mt-0.5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                </svg>
                <div>
                  <h4 className="font-semibold mb-1">Phone Support</h4>
                  <a href="tel:+1-302-222-2222" className="text-primary hover:underline">+1 (302) 222-2222</a>
                  <p className="text-sm text-text mt-1">Mon-Fri, 9am-5pm ET</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <svg className="w-5 h-5 text-primary mt-0.5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                </svg>
                <div>
                  <h4 className="font-semibold mb-1">Live Chat</h4>
                  <p className="text-sm text-text">Available in the <a href="https://app.reasonshield.com/vendor-portal" className="text-primary hover:underline">Vendor Portal</a></p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VendorSupportPage; 